import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../axiosInstance.js';
import TagsInput from './TagsInput.js';
import MyUserContext from '../myUserContext.js';
import ArtEventDropdown from './ArtEventDropdown'; // Import ArtEventDropdown component

const CardEventArtChatMini = ({ art, onFirstTagAdded }) => {
  const myUser = useContext(MyUserContext);

  // For my event.
  const [artEvent, setArtEvent] = useState(null);
  const [myEvent, setMyEvent] = useState(null);
  useEffect(() => {
    if (art.myEventId) {
      axiosInstance
        .get(`/artEvents/${art.myEventId}`)
        .then((response) => {
          setArtEvent(response.data);
          setMyEvent(response.data.event);
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [art.myEventId]);

  const language = myUser ? myUser.myUserSettings.lang : 'ru';

  const [isArtEventDropdownOpen, setIsArtEventDropdownOpen] = useState(false); // State for dropdown visibility

  return (
    <div className="mx-auto max-w-xl">

      {/* Row: [prompt?] [tags] [chat icon] — visually a single pill like the
          paperplane / calendar rows on ArtPage. Clicking anywhere in
          the row opens the chat (when there's a myEvent to chat
          about). Tag clicks stopPropagation so tagging still works
          without also firing the chat modal. Prompt shows inside the
          pill when the art isn't yet in the user's library. */}
      <div className="flex justify-center">
      <div
        role={myEvent ? "button" : undefined}
        tabIndex={myEvent ? 0 : undefined}
        onClick={myEvent ? () => setIsArtEventDropdownOpen(true) : undefined}
        onKeyDown={myEvent ? ((e) => { if (e.key === 'Enter' || e.key === ' ') setIsArtEventDropdownOpen(true); }) : undefined}
        className={myEvent
          ? "flex flex-col items-center gap-1 px-3 py-3 rounded-full transition-colors cursor-pointer [&_*]:cursor-pointer"
          : "flex flex-col items-center gap-1"}
        style={myEvent ? { background: 'var(--mantine-color-default)', minHeight: '48px' } : undefined}
        onMouseEnter={myEvent ? ((e) => { e.currentTarget.style.background = 'var(--mantine-color-default-hover)'; }) : undefined}
        onMouseLeave={myEvent ? ((e) => { e.currentTarget.style.background = 'var(--mantine-color-default)'; }) : undefined}
        title={myEvent ? (language === "ru" ? "Перейти к моему чату" : "Go to my discussion") : undefined}
      >
        {!art.myEventId && !myEvent && (() => {
          const noun = art.category === 1
            ? (language === "ru" ? "фильм" : "movie")
            : art.category === 2
              ? (language === "ru" ? "игру" : "game")
              : art.category === 3
                ? (language === "ru" ? "книгу" : "book")
                : (language === "ru" ? "объект" : "item");
          const text = language === "ru"
            ? `Выберите тэг, чтобы добавить ${noun} в свою библиотеку`
            : `Select tag to add the ${noun} to your library`;
          return (
            <span className="text-sm text-center" style={{ color: 'var(--mantine-color-dimmed)' }}>{text}</span>
          );
        })()}
        <div className="flex items-center gap-2">
          <TagsInput
            key={`${"myEvent"}-${Date.now()}`} // Force re-render with unique key
            language={language}
            // Once the art is in the user's library (myEvent exists),
            // tags become display-only on the art page — real editing
            // happens inside the chat overlay (opens via a click
            // anywhere on this pill). Before the first tag exists, tags
            // stay inline-selectable so the user can create the event.
            editable={!myEvent}
            flush
            artCategory={art.category}
            initialTags={myEvent?.eventTags?.map((tag) => tag.name) || []}
            initialTagObjects={myEvent?.eventTags || []}
            onTagsChange={(actualTagNames) => {
              if (myEvent === null) {
                const pickedTag = actualTagNames[0];
                axiosInstance
                  .post(`/artEvents`, { globalManualTag: pickedTag, artId: art.id, type: "like" /*todo: remove once backend is ready, type is legacy*/ })
                  .then((response) => {
                    setMyEvent(response.data.event);
                    if (onFirstTagAdded) onFirstTagAdded(pickedTag);
                  })
                  .catch((error) => {
                    console.error(error);
                  });
                return;
              } else {
                // Detect a "still not consumed → now consumed" transition
                // (previous global manual tag was todo/inProgress and the
                // new one is neither). That's the same moment the
                // first-tag callback fires for — surface the logbook
                // hint to nudge them to log a date.
                const GLOBAL_MANUAL_TAGS = ["like", "veryGood", "good", "finished", "bad", "noRating", "inProgress", "todo"];
                const prevGmt = (myEvent.eventTags || []).map(t => t.name).find(n => GLOBAL_MANUAL_TAGS.includes(n));
                const nextGmt = actualTagNames.find(n => GLOBAL_MANUAL_TAGS.includes(n));
                const consumedNow = (prevGmt === "todo" || prevGmt === "inProgress")
                  && nextGmt && nextGmt !== "todo" && nextGmt !== "inProgress";
                axiosInstance
                  .post(`/artEvents/tag/${myEvent.id}/tag`, { tags: actualTagNames })
                  .then((response) => {
                    setMyEvent(response.data.event);
                    if (consumedNow && onFirstTagAdded) onFirstTagAdded(nextGmt);
                  })
                  .catch((error) => {
                    console.error(error);
                  });
              }
            }}
          />
          {myEvent && (
            <img
              src="/chat_icon.png"
              alt={language === "ru" ? "Перейти к моему чату" : "Go to my discussion"}
              className="w-6 h-6"
              style={{ filter: 'var(--icon-filter)' }}
            />
          )}
        </div>
      </div>
      </div>
      {isArtEventDropdownOpen && (
        <ArtEventDropdown
          eventId={myEvent.id} // Pass myEvent.id explicitly
          onClose={() => setIsArtEventDropdownOpen(false)} // Close dropdown handler
          language={language}
          eventType="artEvent"
          initialArtEvent={artEvent}
          art={art}
          // Keep the first-row tag pill in sync with edits made inside
          // the overlay — without this, the overlay updates only its
          // local state and the outer row (and next overlay open, which
          // re-seeds from initialArtEvent) shows the stale tag.
          onEventUpdated={(updatedEvent) => {
            setMyEvent(updatedEvent);
            setArtEvent((prev) => (prev ? { ...prev, event: updatedEvent } : prev));
          }}
        />
      )}
    </div>
  );
};

export default CardEventArtChatMini;